import { DOMAIN, urlParam } from "./config";

//productData (optional): {product_id, product_name, product_quantity,
//price, currency} - forwarded to the CRM, which substitutes the
//{product_id}/{product_name}/{quantity}/{amount}/{currency} placeholders
//in the affiliates' pixel HTML.
//extra (optional): more form fields for the CRM, e.g. the pageView's
//navigation timings.
function requestPixels(type, productData, extra) {
	const body = new URLSearchParams({
		transaction_id: urlParam("transaction_id"),
		type,
	});

	if (extra) {
		Object.keys(extra).forEach((key) => {
			body.set(key, extra[key]);
		});
	}

	if (productData) {
		if (productData.product_id != null) {
			body.set("product_id", productData.product_id);
		}
		if (productData.product_name != null) {
			body.set("product_name", productData.product_name);
		}
		if (productData.product_quantity != null) {
			body.set("quantity", productData.product_quantity);
		}
		if (productData.price != null) {
			body.set("amount", productData.price);
		}
		if (productData.currency != null) {
			body.set("currency", productData.currency);
		}
	}

	return fetch(DOMAIN + "/funnels-sdk/get-pixels2", {
		method: "POST",
		body,
	}).then((response) => response.json());
}

//The pageView pixel leaves before the parser has reached <body> - see
//index.js - so the answer can come back with nowhere to inject it yet.
//Holding the injection is enough: the CRM has already counted the view by
//then, and what waits is only the affiliates' HTML.
function whenBodyReady() {
	if (document.body) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		document.addEventListener("DOMContentLoaded", () => resolve(), {
			once: true,
		});
	});
}

//Inject a pixel's HTML into a hidden wrapper. innerHTML alone never runs
//<script> tags, so each one is recreated as a fresh element. Returns the
//img/iframe elements whose load can be awaited before a redirect.
function appendPixelHtml(html) {
	const wrap = document.createElement("div");
	wrap.style.display = "none";
	wrap.innerHTML = html;

	wrap.querySelectorAll("script").forEach((old) => {
		const script = document.createElement("script");
		Array.from(old.attributes).forEach((attr) => {
			script.setAttribute(attr.name, attr.value);
		});
		script.text = old.text;
		old.replaceWith(script);
	});

	document.body.appendChild(wrap);

	return Array.from(wrap.querySelectorAll("img, iframe"));
}

//Fire-and-forget: fetch and inject the pixels for this transaction.
export function firePixel(type, productData, extra) {
	return requestPixels(type, productData, extra)
		.then((pixels) => whenBodyReady().then(() => pixels))
		.then((pixels) => {
			Object.values(pixels || {}).forEach((pixel) => {
				appendPixelHtml(pixel.html);
			});
		})
		.catch(() => {});
}

//Like firePixel, but resolves only once the injected img/iframe pixels
//finished loading - or after maxWaitMs, whichever comes first. Use it when
//a redirect follows, so the navigation doesn't cancel the pixel requests.
export function firePixelAndWait(type, maxWaitMs, productData) {
	return new Promise((resolve) => {
		let resolved = false;
		const finish = () => {
			if (!resolved) {
				resolved = true;
				resolve();
			}
		};
		setTimeout(finish, maxWaitMs || 1500);

		requestPixels(type, productData)
			.then((pixels) => {
				let pending = 0;
				const onResourceDone = () => {
					pending--;
					if (pending === 0) {
						finish();
					}
				};

				Object.values(pixels || {}).forEach((pixel) => {
					appendPixelHtml(pixel.html).forEach((resource) => {
						if (resource.tagName === "IMG" && resource.complete) {
							return;
						}
						pending++;
						resource.addEventListener("load", onResourceDone);
						resource.addEventListener("error", onResourceDone);
					});
				});

				if (pending === 0) {
					finish();
				}
			})
			.catch(finish);
	});
}
